import UserGuide from '@/components/help/UserGuide';
import { Input, Select, Textarea } from '@/components/ui/FormControls';
import FieldHint from '@/components/common/FieldHint';
import Link from '@/components/common/AccessLink';
import SalesMessagingSettings from '@/components/sales/SalesMessagingSettings';
import PermissionGate from '@/components/common/PermissionGate';
// ─────────────────────────────────────────────
// KLOTHIQ — Master Configuration Page
// ─────────────────────────────────────────────

import { useState } from 'react';
import PageHeader from '@/components/common/PageHeader';
import LoadingState from '@/components/common/LoadingState';
import {
  useAllProductTypes,
  useCreateProductType,
  useSetProductTypeActive,
  useAllAttributes,
  useCreateAttribute,
  useSetAttributeActive,
  useProductTypeAttributes,
  useMapAttributeToProductType,
  useUnmapAttributeFromProductType,
  useAllTargetAudiences,
  useCreateTargetAudience,
  useUpdateTargetAudience,
  useSetTargetAudienceActive,
  useAllAgeGroups,
  useCreateAgeGroup,
  useUpdateAgeGroup,
  useSetAgeGroupActive,
  useAllSizeSets,
  useCreateSizeSet,
  useUpdateSizeSet,
  useSetSizeSetActive,
  useDeleteSizeSet,
  useAllTeams,
  useCreateTeam,
  useUpdateTeam,
  useSetTeamActive,
  useDeleteTeam,
  useDeleteMasterConfigEntry,
  DeletableMasterConfigEntity,
} from '@/hooks/useMasterConfig';
import {
  useAllStaffMembers,
  useCreateStaffMember,
  useUpdateStaffMember,
  useSetStaffMemberActive,
} from '@/hooks/useStaffMembers';
import {
  Package,
  Layers,
  Users,
  Ruler,
  UserCog,
  Plus,
  Check,
  X,
  Loader2,
  Pencil,
  Trash2,
  Power,
  RefreshCw,
  Receipt,
} from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '@/contexts/ConfirmContext';
import { useExpenseCategories, useUpsertExpenseCategory, ExpenseCategory } from '@/hooks/useAccounts';

export default function MasterConfigPage() {
  const [activeTab, setActiveTab] = useState<'types' | 'attributes' | 'audiences' | 'sizes_teams' | 'staff' | 'expenses' | 'messaging'>('types');

  // "All" variants (including inactive) — this is the admin management
  // view, so a deactivated row must stay visible here to be reactivated.
  // See FEAT-MASTER-CONFIG-DELETE-001.
  //
  // These queries respect the global 2-minute staleTime (main.tsx) and
  // refetchOnWindowFocus is off app-wide, so switching tabs on this page
  // alone never re-hits the DB — this page never unmounts between tabs,
  // it's the same component with local `activeTab` state. Data edited
  // directly in the DB (or by someone else) would sit stale until that
  // window passes. `refetch` is pulled out of every query below so tab
  // clicks and the Refresh button can force a real re-fetch on demand,
  // and `isFetching` (true during a background refetch too, unlike
  // `isLoading`) drives the loader.
  const {
    data: productTypes,
    isLoading: loadingTypes,
    isFetching: fetchingTypes,
    refetch: refetchTypes,
  } = useAllProductTypes();
  const {
    data: attributes,
    isLoading: loadingAttrs,
    isFetching: fetchingAttrs,
    refetch: refetchAttrs,
  } = useAllAttributes();
  const { data: targetAudiences, isFetching: fetchingAudiences, refetch: refetchAudiences } = useAllTargetAudiences();
  const { data: ageGroups, isFetching: fetchingAgeGroups, refetch: refetchAgeGroups } = useAllAgeGroups();
  const { data: sizeSets, isFetching: fetchingSizeSets, refetch: refetchSizeSets } = useAllSizeSets();
  const { data: teams, isFetching: fetchingTeams, refetch: refetchTeams } = useAllTeams();
  const { data: staffMembers, isFetching: fetchingStaff, refetch: refetchStaff } = useAllStaffMembers();
  const { data: expenseCategories, isFetching: fetchingCategories, refetch: refetchCategories } = useExpenseCategories(true);
  const upsertExpenseCategory = useUpsertExpenseCategory();

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ExpenseCategory | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryKeywords, setNewCategoryKeywords] = useState('');
  const [newCategorySortOrder, setNewCategorySortOrder] = useState('100');

  const isFetchingAny =
    fetchingTypes || fetchingAttrs || fetchingAudiences || fetchingAgeGroups || fetchingSizeSets || fetchingTeams || fetchingStaff || fetchingCategories;

  const refetchAll = () => {
    refetchTypes();
    refetchAttrs();
    refetchAudiences();
    refetchAgeGroups();
    refetchSizeSets();
    refetchTeams();
    refetchStaff();
    refetchCategories();
  };

  const handleTabChange = (tab: typeof activeTab) => {
    setActiveTab(tab);
    refetchAll();
  };

  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);

  // Auto select first product type safely
  const firstType = productTypes?.[0];
  const firstTypeId = firstType ? ((firstType as any).id || firstType.code || firstType.name) : '';
  const activeTypeId = selectedTypeId || firstTypeId;


  const { data: mappedAttrs, isLoading: loadingMapped } = useProductTypeAttributes(activeTypeId);

  const createProductType = useCreateProductType();
  const createAttribute = useCreateAttribute();
  const mapAttr = useMapAttributeToProductType();
  const unmapAttr = useUnmapAttributeFromProductType();
  const createTeam = useCreateTeam();

  // Modals state
  const [showAddTypeModal, setShowAddTypeModal] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeDesc, setNewTypeDesc] = useState('');

  const [showAddAttrModal, setShowAddAttrModal] = useState(false);
  const [newAttrName, setNewAttrName] = useState('');
  const [newAttrType, setNewAttrType] = useState('select');
  const [newAttrOptions, setNewAttrOptions] = useState('');
  const [newAttrIsVariant, setNewAttrIsVariant] = useState(false);

  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<any>(null);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamSport, setNewTeamSport] = useState('Football');

  const [showAddAudienceModal, setShowAddAudienceModal] = useState(false);
  const [editingAudience, setEditingAudience] = useState<any>(null);
  const [newAudienceName, setNewAudienceName] = useState('');
  const [newAudienceCode, setNewAudienceCode] = useState('');

  const [showAddAgeGroupModal, setShowAddAgeGroupModal] = useState(false);
  const [editingAgeGroup, setEditingAgeGroup] = useState<any>(null);
  const [newAgeGroupName, setNewAgeGroupName] = useState('');
  const [newAgeGroupCode, setNewAgeGroupCode] = useState('');

  const [showAddSizeSetModal, setShowAddSizeSetModal] = useState(false);
  const [editingSizeSet, setEditingSizeSet] = useState<any>(null);
  const [newSizeSetName, setNewSizeSetName] = useState('');
  const [newSizeSetCode, setNewSizeSetCode] = useState('');
  const [newSizeSetSizes, setNewSizeSetSizes] = useState('');

  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState<any>(null);
  const [newStaffName, setNewStaffName] = useState('');
  const [newStaffRoleLabel, setNewStaffRoleLabel] = useState('');

  const createTargetAudience = useCreateTargetAudience();
  const updateTargetAudience = useUpdateTargetAudience();
  const createAgeGroup = useCreateAgeGroup();
  const updateAgeGroup = useUpdateAgeGroup();
  const createSizeSet = useCreateSizeSet();
  const updateSizeSet = useUpdateSizeSet();
  const updateTeam = useUpdateTeam();
  const createStaffMember = useCreateStaffMember();
  const updateStaffMember = useUpdateStaffMember();
  const setStaffMemberActive = useSetStaffMemberActive();

  // FEAT-MASTER-CONFIG-DELETE-001
  const confirm = useConfirm();
  const setProductTypeActive = useSetProductTypeActive();
  const setAttributeActive = useSetAttributeActive();
  const setTargetAudienceActive = useSetTargetAudienceActive();
  const setAgeGroupActive = useSetAgeGroupActive();
  const setSizeSetActive = useSetSizeSetActive();
  const setTeamActive = useSetTeamActive();
  const deleteTeam = useDeleteTeam();
  const deleteSizeSet = useDeleteSizeSet();
  const deleteMasterConfigEntry = useDeleteMasterConfigEntry();

  // Tier A — Teams & Size Sets: no FK references them anywhere, so delete
  // is unconditional (still type-to-confirm, per spec FR-004).
  const handleDeleteTierA = async (
    kind: 'team' | 'size_set',
    id: string,
    name: string
  ) => {
    const ok = await confirm({
      title: `Delete "${name}"?`,
      description: `This cannot be undone. Type the name below to confirm.`,
      confirmText: 'Delete',
      variant: 'destructive',
      confirmInputValue: name,
    });
    if (!ok) return;
    try {
      if (kind === 'team') await deleteTeam.mutateAsync(id);
      else await deleteSizeSet.mutateAsync(id);
      toast.success(`"${name}" deleted`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  // Tier B/C — usage-checked via delete_master_config_entry RPC. The RPC
  // refuses (with a usage count) rather than deleting when the entry is
  // still in use — caught here and surfaced as-is, since the RPC's
  // message is already human-readable (spec FR-002).
  const handleDeleteTierBC = async (
    entity: DeletableMasterConfigEntity,
    id: string,
    name: string,
    label: string
  ) => {
    const ok = await confirm({
      title: `Delete ${label} "${name}"?`,
      description: `This cannot be undone. Type the name below to confirm.`,
      confirmText: 'Delete',
      variant: 'destructive',
      confirmInputValue: name,
    });
    if (!ok) return;
    try {
      await deleteMasterConfigEntry.mutateAsync({ entity, id });
      toast.success(`"${name}" deleted`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  // Deactivate/Reactivate — fully reversible, so a plain confirm (no
  // type-to-confirm input) is proportionate (spec FR-003).
  const handleToggleProductTypeActive = async (id: string, name: string, currentlyActive: boolean) => {
    try {
      await setProductTypeActive.mutateAsync({ id, is_active: !currentlyActive });
      toast.success(currentlyActive ? `"${name}" deactivated` : `"${name}" reactivated`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update');
    }
  };

  const handleToggleAttributeActive = async (id: string, name: string, currentlyActive: boolean) => {
    try {
      await setAttributeActive.mutateAsync({ id, is_active: !currentlyActive });
      toast.success(currentlyActive ? `"${name}" deactivated` : `"${name}" reactivated`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update');
    }
  };

  const handleToggleAudienceActive = async (id: string, name: string, currentlyActive: boolean) => {
    try {
      await setTargetAudienceActive.mutateAsync({ id, is_active: !currentlyActive });
      toast.success(currentlyActive ? `"${name}" deactivated` : `"${name}" reactivated`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update');
    }
  };

  const handleToggleAgeGroupActive = async (id: string, name: string, currentlyActive: boolean) => {
    try {
      await setAgeGroupActive.mutateAsync({ id, is_active: !currentlyActive });
      toast.success(currentlyActive ? `"${name}" deactivated` : `"${name}" reactivated`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update');
    }
  };

  const handleToggleSizeSetActive = async (id: string, name: string, currentlyActive: boolean) => {
    try {
      await setSizeSetActive.mutateAsync({ id, is_active: !currentlyActive });
      toast.success(currentlyActive ? `"${name}" deactivated` : `"${name}" reactivated`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update');
    }
  };

  const handleToggleTeamActive = async (id: string, name: string, currentlyActive: boolean) => {
    try {
      await setTeamActive.mutateAsync({ id, is_active: !currentlyActive });
      toast.success(currentlyActive ? `"${name}" deactivated` : `"${name}" reactivated`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update');
    }
  };

  const handleCreateProductType = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTypeName.trim()) return;
    try {
      await createProductType.mutateAsync({ name: newTypeName.trim(), description: newTypeDesc.trim() });
      toast.success(`Product Type "${newTypeName}" created successfully`);
      setNewTypeName('');
      setNewTypeDesc('');
      setShowAddTypeModal(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to create product type');
    }
  };

  const handleCreateAttribute = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAttrName.trim()) return;
    try {
      const opts = newAttrType === 'select'
        ? newAttrOptions.split(',').map(s => s.trim()).filter(Boolean)
        : null;

      await createAttribute.mutateAsync({
        name: newAttrName.trim(),
        input_type: newAttrType,
        options: opts,
        is_variant_level: newAttrIsVariant,
      });
      toast.success(`Attribute "${newAttrName}" created successfully`);
      setNewAttrName('');
      setNewAttrOptions('');
      setShowAddAttrModal(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to create attribute');
    }
  };

  const handleToggleAttributeMapping = async (attributeId: string, currentlyMapped: boolean, isReq: boolean = false) => {
    if (!activeTypeId) return;
    try {
      if (currentlyMapped) {
        await unmapAttr.mutateAsync({ product_type_id: activeTypeId, attribute_id: attributeId });
        toast.success('Attribute removed from product type');
      } else {
        await mapAttr.mutateAsync({ product_type_id: activeTypeId, attribute_id: attributeId, is_required: isReq });
        toast.success('Attribute mapped to product type');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to update mapping');
    }
  };

  const handleToggleAttributeRequired = async (attributeId: string, currentReq: boolean) => {
    if (!activeTypeId) return;
    try {
      await mapAttr.mutateAsync({ product_type_id: activeTypeId, attribute_id: attributeId, is_required: !currentReq });
      toast.success(`Attribute marked as ${!currentReq ? 'Required' : 'Optional'}`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update required status');
    }
  };

  const handleSaveTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;
    try {
      if (editingTeam && editingTeam.id) {
        await updateTeam.mutateAsync({ id: editingTeam.id, name: newTeamName.trim(), sport: newTeamSport });
        toast.success(`Team "${newTeamName}" updated successfully`);
      } else {
        await createTeam.mutateAsync({ name: newTeamName.trim(), sport: newTeamSport, code: editingTeam?.code });
        toast.success(`Team "${newTeamName}" added successfully`);
      }
      setEditingTeam(null);
      setNewTeamName('');
      setShowAddTeamModal(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to save team');
    }
  };

  const handleSaveStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newStaffName.trim()) return;
    try {
      if (editingStaff && editingStaff.id) {
        await updateStaffMember.mutateAsync({
          id: editingStaff.id,
          name: newStaffName.trim(),
          role_label: newStaffRoleLabel.trim() || undefined,
        });
        toast.success(`Staff member "${newStaffName}" updated successfully`);
      } else {
        await createStaffMember.mutateAsync({
          name: newStaffName.trim(),
          role_label: newStaffRoleLabel.trim() || undefined,
        });
        toast.success(`Staff member "${newStaffName}" added successfully`);
      }
      setEditingStaff(null);
      setNewStaffName('');
      setNewStaffRoleLabel('');
      setShowAddStaffModal(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to save staff member');
    }
  };

  const handleToggleStaffActive = async (staffId: string, currentlyActive: boolean) => {
    try {
      await setStaffMemberActive.mutateAsync({ id: staffId, is_active: !currentlyActive });
      toast.success(currentlyActive ? 'Staff member deactivated' : 'Staff member reactivated');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update staff member');
    }
  };

  const handleSaveAudience = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAudienceName.trim()) return;
    try {
      const codeToSave = newAudienceCode.trim() || editingAudience?.code || undefined;
      if (editingAudience && editingAudience.id) {
        await updateTargetAudience.mutateAsync({ id: editingAudience.id, name: newAudienceName.trim(), code: codeToSave });
        toast.success(`Target Audience "${newAudienceName}" updated successfully`);
      } else {
        await createTargetAudience.mutateAsync({ name: newAudienceName.trim(), code: codeToSave });
        toast.success(`Target Audience "${newAudienceName}" added successfully`);
      }
      setEditingAudience(null);
      setNewAudienceName('');
      setNewAudienceCode('');
      setShowAddAudienceModal(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to save target audience');
    }
  };

  const handleSaveAgeGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAgeGroupName.trim()) return;
    try {
      const codeToSave = newAgeGroupCode.trim() || editingAgeGroup?.code || undefined;
      if (editingAgeGroup && editingAgeGroup.id) {
        await updateAgeGroup.mutateAsync({ id: editingAgeGroup.id, name: newAgeGroupName.trim(), code: codeToSave });
        toast.success(`Age Group "${newAgeGroupName}" updated successfully`);
      } else {
        await createAgeGroup.mutateAsync({ name: newAgeGroupName.trim(), code: codeToSave });
        toast.success(`Age Group "${newAgeGroupName}" added successfully`);
      }
      setEditingAgeGroup(null);
      setNewAgeGroupName('');
      setNewAgeGroupCode('');
      setShowAddAgeGroupModal(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to save age group');
    }
  };

  const handleSaveSizeSet = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSizeSetName.trim() || !newSizeSetSizes.trim()) return;
    try {
      const sizesArray = newSizeSetSizes.split(',').map(s => s.trim()).filter(Boolean);
      const codeToSave = newSizeSetCode.trim() || editingSizeSet?.code || undefined;
      if (editingSizeSet && editingSizeSet.id) {
        await updateSizeSet.mutateAsync({ id: editingSizeSet.id, name: newSizeSetName.trim(), code: codeToSave, sizes: sizesArray });
        toast.success(`Size Set "${newSizeSetName}" updated successfully`);
      } else {
        await createSizeSet.mutateAsync({ name: newSizeSetName.trim(), code: codeToSave, sizes: sizesArray });
        toast.success(`Size Set "${newSizeSetName}" added successfully`);
      }
      setEditingSizeSet(null);
      setNewSizeSetName('');
      setNewSizeSetCode('');
      setNewSizeSetSizes('');
      setShowAddSizeSetModal(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to save size set');
    }
  };

  if (loadingTypes || loadingAttrs) return <LoadingState message="Loading catalog configuration..." />;

  return (
    <div>
      <Link
        to="/master-config/catalog"
        className="mb-4 inline-flex min-h-11 items-center rounded-xl border border-border px-4 text-sm text-primary"
      >
        Configure category fields
      </Link>
      <p className="mb-4 text-sm text-muted-foreground">Category setup controls new field versions. Legacy attribute mappings remain available for older product layouts.</p>
      <PageHeader
        title="Master Configuration"
        description="Centralized catalog architecture: Product Categories, Dynamic Attributes, Audiences, Size Sets, and Teams"
        actions={
          <button
            type="button"
            onClick={refetchAll}
            disabled={isFetchingAny}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent disabled:opacity-60"
            title="Refresh all data"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetchingAny ? 'animate-spin' : ''}`} />
            {isFetchingAny ? 'Refreshing…' : 'Refresh'}
          </button>
        }
      />

      {/* Tabs Header */}
      <div className="mb-6 border-b border-border">
        <nav className="flex gap-2 overflow-x-auto" aria-label="Master Config Tabs">
          {[
            { id: 'types', label: 'Product Categories & Mapping', icon: <Package className="h-4 w-4" /> },
            { id: 'attributes', label: 'Attributes & Options', icon: <Layers className="h-4 w-4" /> },
            { id: 'audiences', label: 'Target Audiences & Age Groups', icon: <Users className="h-4 w-4" /> },
            { id: 'sizes_teams', label: 'Size Sets & Teams', icon: <Ruler className="h-4 w-4" /> },
            { id: 'staff', label: 'Staff', icon: <UserCog className="h-4 w-4" /> },
            {id:'messaging',label:'Sales messaging',icon:<Receipt className="h-4 w-4"/>},
            { id: 'expenses', label: 'Expense Categories', icon: <Receipt className="h-4 w-4" /> },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id as typeof activeTab)}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'
              }`}
            >
              {tab.icon}
              {tab.label}
              {isFetchingAny && activeTab === tab.id && <Loader2 className="h-3 w-3 animate-spin" />}
            </button>
          ))}
        </nav>
      </div>

      {/* TAB 1: PRODUCT TYPES & MAPPING */}
      {activeTab === 'messaging'&&<SalesMessagingSettings/>}
      {activeTab === 'types' && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Left Column: Product Categories List */}
          <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground">Product Categories</h2>
              <PermissionGate permission="master_config.manage"><button
                onClick={() => setShowAddTypeModal(true)}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Type
              </button></PermissionGate>
            </div>

            <div className="space-y-2">
              {productTypes?.map(type => {
                const typeId = (type as any).id || type.code || type.name;
                const isSelected = activeTypeId === typeId;
                const isActive = (type as any).is_active !== false;

                return (
                  <div
                    key={typeId}
                    className={`group w-full rounded-md border p-3 text-sm transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/10 font-semibold text-primary'
                        : 'border-border/60 bg-background hover:bg-accent hover:text-accent-foreground'
                    } ${!isActive ? 'opacity-60' : ''}`}
                  >
                    <button type="button" onClick={() => setSelectedTypeId(typeId)} className="w-full text-left">
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-1.5">
                          {type.name}
                          {!isActive && (
                            <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] font-normal uppercase text-muted-foreground">Inactive</span>
                          )}
                        </span>
                        {type.code && (
                          <span className="font-mono text-[10px] uppercase text-muted-foreground">{type.code}</span>
                        )}
                      </div>
                      {type.description && (
                        <p className="mt-1 text-xs text-muted-foreground font-normal line-clamp-1">
                          {type.description}
                        </p>
                      )}
                    </button>
                    <div className="mt-2 flex items-center gap-3 border-t border-border/40 pt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <PermissionGate permission="master_config.manage"><button
                        type="button"
                        onClick={() => handleToggleProductTypeActive(typeId, type.name, isActive)}
                        className="text-[11px] font-medium text-muted-foreground hover:text-primary"
                      >
                        {isActive ? 'Deactivate' : 'Reactivate'}
                      </button></PermissionGate>
                      <PermissionGate permission="master_config.manage"><button
                        type="button"
                        onClick={() => handleDeleteTierBC('product_type', typeId, type.name, 'Product Type')}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" /> Delete
                      </button></PermissionGate>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Column: Attribute Mapping Matrix */}
          <div className="rounded-lg border border-border bg-card p-5 shadow-sm lg:col-span-2">
            <div className="mb-4 border-b border-border pb-3">
              <h2 className="text-base font-semibold text-foreground">
                Assigned Attributes Matrix
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Configure which dynamic attributes appear on the Product form when this Product Type is selected.
              </p>
            </div>

            {loadingMapped ? (
              <LoadingState message="Loading attribute mapping..." />
            ) : (
              <div className="space-y-3">
                {attributes?.map(attr => {
                  const attrId = attr.id || attr.code || attr.name;
                  const mapped = mappedAttrs?.find((m: any) => m.id === attrId || m.name === attr.name || m.code === attr.code);
                  const isMapped = !!mapped;
                  const isReq = mapped?.is_required ?? false;

                  return (
                    <div
                      key={attrId}
                      className={`flex flex-col gap-3 rounded-md border p-3.5 transition-all sm:flex-row sm:items-center sm:justify-between ${
                        isMapped
                          ? 'border-primary/40 bg-primary/5'
                          : 'border-border bg-background hover:bg-accent/30'
                      }`}
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-foreground">{attr.name}</span>
                          {attr.is_variant_level && (
                            <span className="rounded bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-medium text-blue-600 dark:text-blue-400">
                              Variant Attribute
                            </span>
                          )}
                          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase font-mono text-muted-foreground">
                            {attr.input_type}
                          </span>
                        </div>
                        {attr.options && Array.isArray(attr.options) && (
                          <p className="mt-1 text-xs text-muted-foreground line-clamp-1">
                            Values: {attr.options.join(', ')}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-3">
                        {isMapped && (
                          <PermissionGate permission="master_config.manage"><label className="flex items-center gap-1.5 text-xs font-medium text-foreground cursor-pointer select-none">
                            <Input
                              type="checkbox"
                              checked={isReq}
                              onChange={() => handleToggleAttributeRequired(attrId, isReq)}
                              className="rounded border-input text-primary focus:ring-primary h-4 w-4"
                              helpLabel="Required Field"
                            />
                            Required Field
                          </label></PermissionGate>
                        )}

                        <PermissionGate permission="master_config.manage"><button
                          onClick={() => handleToggleAttributeMapping(attrId, isMapped, false)}
                          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                            isMapped
                              ? 'bg-destructive/10 text-destructive hover:bg-destructive/20 border border-destructive/30'
                              : 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm'
                          }`}
                        >
                          {isMapped ? (
                            <>
                              <X className="h-3.5 w-3.5" /> Remove
                            </>
                          ) : (
                            <>
                              <Plus className="h-3.5 w-3.5" /> Assign to Type
                            </>
                          )}
                        </button></PermissionGate>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 2: ATTRIBUTES & OPTIONS */}
      {activeTab === 'attributes' && (
        <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between border-b border-border pb-3">
            <div>
              <h2 className="text-base font-semibold text-foreground">Attribute Definitions</h2>
              <p className="text-xs text-muted-foreground">Define dynamic fields and selectable options used across Product Categories.</p>
            </div>
            <PermissionGate permission="master_config.manage"><button
              onClick={() => setShowAddAttrModal(true)}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              New Attribute
            </button></PermissionGate>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Attribute Name</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Input Type</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Scope</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Predefined Values</th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {attributes?.map(attr => {
                  const attrId = (attr as any).id;
                  const isActive = (attr as any).is_active !== false;
                  return (
                  <tr key={attr.id || attr.name} className="hover:bg-muted/20">
                    <td className="px-4 py-3 font-medium text-foreground">{attr.name}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs uppercase rounded bg-muted px-2 py-0.5">{attr.input_type}</span>
                    </td>
                    <td className="px-4 py-3">
                      {attr.is_variant_level ? (
                        <span className="rounded bg-blue-500/10 px-2 py-0.5 text-xs font-semibold text-blue-600 dark:text-blue-400">
                          Variant Level
                        </span>
                      ) : (
                        <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                          Product Level
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {attr.options && Array.isArray(attr.options) && attr.options.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {attr.options.map((opt: string) => (
                            <span key={opt} className="rounded border border-border bg-background px-1.5 py-0.5">
                              {opt}
                            </span>
                          ))}
                        </div>
                      ) : (
                        '— (Free Text / Custom)'
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isActive ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                          <Check className="h-3 w-3" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-3">
                        <PermissionGate permission="master_config.manage"><button
                          onClick={() => handleToggleAttributeActive(attrId, attr.name, isActive)}
                          className="text-xs font-medium text-muted-foreground hover:text-primary"
                        >
                          {isActive ? 'Deactivate' : 'Reactivate'}
                        </button></PermissionGate>
                        <PermissionGate permission="master_config.manage"><button
                          onClick={() => handleDeleteTierBC('attribute', attrId, attr.name, 'Attribute')}
                          className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-destructive"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button></PermissionGate>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 3: TARGET AUDIENCES & AGE GROUPS */}
      {activeTab === 'audiences' && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Target Audiences */}
          <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between border-b border-border pb-3">
              <div>
                <h2 className="text-base font-semibold text-foreground">Target Audience (Gender)</h2>
                <p className="text-xs text-muted-foreground">Reusable audience classifications for products.</p>
              </div>
              <PermissionGate permission="master_config.manage"><button
                onClick={() => {
                  setEditingAudience(null);
                  setNewAudienceName('');
                  setNewAudienceCode('');
                  setShowAddAudienceModal(true);
                }}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
              >
                <Plus className="h-3.5 w-3.5" /> Add Audience
              </button></PermissionGate>
            </div>
            <div className="space-y-2">
              {targetAudiences?.map(aud => {
                const audId = (aud as any).id;
                const isActive = (aud as any).is_active !== false;
                return (
                <div key={aud.id || aud.code} className={`group flex items-center justify-between rounded-md border border-border bg-background p-3 text-sm hover:border-primary/50 transition-colors ${!isActive ? 'opacity-60' : ''}`}>
                  <span className="flex items-center gap-1.5 font-medium text-foreground">
                    {aud.name}
                    {!isActive && <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] font-normal uppercase text-muted-foreground">Inactive</span>}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs uppercase text-muted-foreground">{aud.code}</span>
                    <PermissionGate permission="master_config.manage"><button
                      onClick={() => {
                        setEditingAudience(aud);
                        setNewAudienceName(aud.name);
                        setNewAudienceCode(aud.code || '');
                        setShowAddAudienceModal(true);
                      }}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary transition-opacity p-1"
                      title="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button></PermissionGate>
                    <PermissionGate permission="master_config.manage"><button
                      onClick={() => handleToggleAudienceActive(audId, aud.name, isActive)}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary transition-opacity p-1"
                      title={isActive ? 'Deactivate' : 'Reactivate'}
                    >
                      <Power className="h-3.5 w-3.5" />
                    </button></PermissionGate>
                    <PermissionGate permission="master_config.manage"><button
                      onClick={() => handleDeleteTierBC('target_audience', audId, aud.name, 'Target Audience')}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity p-1"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button></PermissionGate>
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          {/* Age Groups */}
          <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between border-b border-border pb-3">
              <div>
                <h2 className="text-base font-semibold text-foreground">Age Groups</h2>
                <p className="text-xs text-muted-foreground">Age group brackets for sizing and catalog filtering.</p>
              </div>
              <PermissionGate permission="master_config.manage"><button
                onClick={() => {
                  setEditingAgeGroup(null);
                  setNewAgeGroupName('');
                  setNewAgeGroupCode('');
                  setShowAddAgeGroupModal(true);
                }}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
              >
                <Plus className="h-3.5 w-3.5" /> Add Age Group
              </button></PermissionGate>
            </div>
            <div className="space-y-2">
              {ageGroups?.map(group => {
                const groupId = (group as any).id;
                const isActive = (group as any).is_active !== false;
                return (
                <div key={group.id || group.code} className={`group flex items-center justify-between rounded-md border border-border bg-background p-3 text-sm hover:border-primary/50 transition-colors ${!isActive ? 'opacity-60' : ''}`}>
                  <span className="flex items-center gap-1.5 font-medium text-foreground">
                    {group.name}
                    {!isActive && <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] font-normal uppercase text-muted-foreground">Inactive</span>}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs uppercase text-muted-foreground">{group.code}</span>
                    <PermissionGate permission="master_config.manage"><button
                      onClick={() => {
                        setEditingAgeGroup(group);
                        setNewAgeGroupName(group.name);
                        setNewAgeGroupCode(group.code || '');
                        setShowAddAgeGroupModal(true);
                      }}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary transition-opacity p-1"
                      title="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button></PermissionGate>
                    <PermissionGate permission="master_config.manage"><button
                      onClick={() => handleToggleAgeGroupActive(groupId, group.name, isActive)}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary transition-opacity p-1"
                      title={isActive ? 'Deactivate' : 'Reactivate'}
                    >
                      <Power className="h-3.5 w-3.5" />
                    </button></PermissionGate>
                    <PermissionGate permission="master_config.manage"><button
                      onClick={() => handleDeleteTierBC('age_group', groupId, group.name, 'Age Group')}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity p-1"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button></PermissionGate>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: SIZE SETS & TEAMS */}
      {activeTab === 'sizes_teams' && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Size Sets */}
          <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between border-b border-border pb-3">
              <div>
                <h2 className="text-base font-semibold text-foreground">Size Sets Configuration</h2>
                <p className="text-xs text-muted-foreground">Pre-configured size systems for different apparel types.</p>
              </div>
              <PermissionGate permission="master_config.manage"><button
                onClick={() => {
                  setEditingSizeSet(null);
                  setNewSizeSetName('');
                  setNewSizeSetCode('');
                  setNewSizeSetSizes('');
                  setShowAddSizeSetModal(true);
                }}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
              >
                <Plus className="h-3.5 w-3.5" /> Add Size Set
              </button></PermissionGate>
            </div>
            <div className="space-y-3">
              {sizeSets?.map(set => {
                const setId = (set as any).id;
                const isActive = (set as any).is_active !== false;
                return (
                <div key={set.id || set.code} className={`group rounded-md border border-border bg-background p-3.5 hover:border-primary/50 transition-colors ${!isActive ? 'opacity-60' : ''}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="flex items-center gap-1.5 font-semibold text-sm text-foreground">
                      {set.name}
                      {!isActive && <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] font-normal uppercase text-muted-foreground">Inactive</span>}
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs uppercase text-muted-foreground">{set.code}</span>
                      <PermissionGate permission="master_config.manage"><button
                        onClick={() => {
                          setEditingSizeSet(set);
                          setNewSizeSetName(set.name);
                          setNewSizeSetCode(set.code || '');
                          setNewSizeSetSizes(Array.isArray(set.sizes) ? set.sizes.join(', ') : '');
                          setShowAddSizeSetModal(true);
                        }}
                        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary transition-opacity p-1 -mr-1"
                        title="Edit"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button></PermissionGate>
                      <PermissionGate permission="master_config.manage"><button
                        onClick={() => handleToggleSizeSetActive(setId, set.name, isActive)}
                        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary transition-opacity p-1 -mr-1"
                        title={isActive ? 'Deactivate' : 'Reactivate'}
                      >
                        <Power className="h-3.5 w-3.5" />
                      </button></PermissionGate>
                      <PermissionGate permission="master_config.manage"><button
                        onClick={() => handleDeleteTierA('size_set', setId, set.name)}
                        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity p-1 -mr-1"
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button></PermissionGate>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {set.sizes && Array.isArray(set.sizes) && set.sizes.map((sz: string) => (
                      <span key={sz} className="rounded bg-primary/10 px-2 py-0.5 font-mono text-xs font-semibold text-primary">
                        {sz}
                      </span>
                    ))}
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          {/* Teams */}
          <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between border-b border-border pb-3">
              <div>
                <h2 className="text-base font-semibold text-foreground">Sports & Teams Master</h2>
                <p className="text-xs text-muted-foreground">Teams used for clothing categories and brand collections.</p>
              </div>
              <PermissionGate permission="master_config.manage"><button
                onClick={() => {
                  setEditingTeam(null);
                  setNewTeamName('');
                  setNewTeamSport('Football');
                  setShowAddTeamModal(true);
                }}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
              >
                <Plus className="h-3.5 w-3.5" /> Add Team
              </button></PermissionGate>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {teams?.map(t => {
                const teamId = (t as any).id;
                const isActive = (t as any).is_active !== false;
                return (
                <div key={t.id || t.code} className={`group flex items-center justify-between rounded border border-border bg-background px-3 py-2 text-sm hover:border-primary/50 transition-colors ${!isActive ? 'opacity-60' : ''}`}>
                  <span className="flex items-center gap-1.5 font-medium text-foreground">
                    {t.name}
                    {!isActive && <span className="rounded bg-muted px-1 py-0.5 text-[9px] font-normal uppercase text-muted-foreground">Inactive</span>}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground">{t.sport}</span>
                    <PermissionGate permission="master_config.manage"><button
                      onClick={() => {
                        setEditingTeam(t);
                        setNewTeamName(t.name);
                        setNewTeamSport(t.sport || '');
                        setShowAddTeamModal(true);
                      }}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary transition-opacity p-0.5"
                      title="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button></PermissionGate>
                    <PermissionGate permission="master_config.manage"><button
                      onClick={() => handleToggleTeamActive(teamId, t.name, isActive)}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary transition-opacity p-0.5"
                      title={isActive ? 'Deactivate' : 'Reactivate'}
                    >
                      <Power className="h-3.5 w-3.5" />
                    </button></PermissionGate>
                    <PermissionGate permission="master_config.manage"><button
                      onClick={() => handleDeleteTierA('team', teamId, t.name)}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity p-0.5"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button></PermissionGate>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* TAB 5: STAFF */}
      {activeTab === 'staff' && (
        <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between border-b border-border pb-3">
            <div>
              <h2 className="text-base font-semibold text-foreground">Staff Members</h2>
              <p className="text-xs text-muted-foreground">
                Sellers with no system login (e.g. part-time floor staff). Available in the
                Seller field on the Sale Form alongside registered system users.
              </p>
            </div>
            <PermissionGate permission="master_config.manage"><button
              onClick={() => {
                setEditingStaff(null);
                setNewStaffName('');
                setNewStaffRoleLabel('');
                setShowAddStaffModal(true);
              }}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
            >
              <Plus className="h-3.5 w-3.5" /> Add Staff Member
            </button></PermissionGate>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Name</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Role</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {staffMembers?.map((s) => (
                  <tr key={s.id} className="hover:bg-muted/20">
                    <td className="px-4 py-3 font-medium text-foreground">{s.name}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{s.role_label || '—'}</td>
                    <td className="px-4 py-3">
                      {s.is_active ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                          <Check className="h-3 w-3" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-3">
                        <PermissionGate permission="master_config.manage"><button
                          onClick={() => {
                            setEditingStaff(s);
                            setNewStaffName(s.name);
                            setNewStaffRoleLabel(s.role_label || '');
                            setShowAddStaffModal(true);
                          }}
                          className="text-muted-foreground hover:text-primary transition-colors p-1"
                          title="Edit"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button></PermissionGate>
                        <PermissionGate permission="master_config.manage"><button
                          onClick={() => handleToggleStaffActive(s.id, s.is_active)}
                          className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
                            s.is_active
                              ? 'border-destructive/30 text-destructive hover:bg-destructive/10'
                              : 'border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/10 dark:text-emerald-400'
                          }`}
                        >
                          {s.is_active ? 'Deactivate' : 'Reactivate'}
                        </button></PermissionGate>
                        <PermissionGate permission="master_config.manage"><button
                          onClick={() => handleDeleteTierBC('staff_member', s.id, s.name, 'Staff Member')}
                          className="text-muted-foreground hover:text-destructive transition-colors p-1"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button></PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))}
                {(!staffMembers || staffMembers.length === 0) && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-sm text-muted-foreground">
                      No staff members yet. Add one to make them selectable as a Seller on the Sale Form.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 6: EXPENSE CATEGORIES */}
      {activeTab === 'expenses' && (
        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Expense Categories</h2>
              <p className="text-xs text-muted-foreground">
                Classify operational expenditures and auto-match spreadsheet imports by keyword.
              </p>
            </div>
            <PermissionGate permission="master_config.manage"><button
              onClick={() => {
                setEditingCategory(null);
                setNewCategoryName('');
                setNewCategoryKeywords('');
                setNewCategorySortOrder('100');
                setShowCategoryModal(true);
              }}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
            >
              <Plus className="h-3.5 w-3.5" />
              Add Category
            </button></PermissionGate>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs font-medium text-muted-foreground">
                  <th className="px-4 py-2">Category Name</th>
                  <th className="px-4 py-2">Keywords (for Auto-Matching)</th>
                  <th className="px-4 py-2">Sort Order</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {expenseCategories?.map((c) => (
                  <tr key={c.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3 font-medium text-foreground">
                      {c.name}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      <div className="flex flex-wrap gap-1">
                        {c.keywords && c.keywords.length > 0 ? (
                          c.keywords.map((kw, idx) => (
                            <span
                              key={idx}
                              className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono"
                            >
                              {kw}
                            </span>
                          ))
                        ) : (
                          <span className="text-[11px] text-muted-foreground/50">None</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {c.sort_order}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {c.is_active ? (
                        <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGate permission="master_config.manage"><button
                          onClick={() => {
                            setEditingCategory(c);
                            setNewCategoryName(c.name);
                            setNewCategoryKeywords((c.keywords ?? []).join(', '));
                            setNewCategorySortOrder(String(c.sort_order));
                            setShowCategoryModal(true);
                          }}
                          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                          title="Edit Category"
                        >
                          <Pencil className="h-4 w-4" />
                        </button></PermissionGate>
                        <button
                          onClick={async () => {
                            try {
                              await upsertExpenseCategory.mutateAsync({
                                id: c.id,
                                name: c.name,
                                keywords: c.keywords ?? [],
                                sort_order: c.sort_order,
                                is_active: !c.is_active,
                              });
                              toast.success(`Category "${c.name}" ${c.is_active ? 'deactivated' : 'activated'}`);
                              refetchCategories();
                            } catch (err: unknown) {
                              toast.error(err instanceof Error ? err.message : 'Failed to update category');
                            }
                          }}
                          className={`rounded p-1 ${
                            c.is_active
                              ? 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
                              : 'text-emerald-600 hover:bg-emerald-500/10'
                          }`}
                          title={c.is_active ? 'Deactivate Category' : 'Activate Category'}
                        >
                          <Power className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {(!expenseCategories || expenseCategories.length === 0) && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-sm text-muted-foreground">
                      No expense categories found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODAL: ADD / EDIT EXPENSE CATEGORY */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-field-guide-scope>
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg animate-in fade-in zoom-in-95">
            <div className="flex items-start justify-between gap-3 pr-8">
              <h3 className="mb-4 text-base font-semibold text-foreground">
                {editingCategory ? 'Edit Expense Category' : 'Add Expense Category'}
              </h3>
              <UserGuide compact />
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const trimmed = newCategoryName.trim();
                if (!trimmed) {
                  toast.error('Category name is required');
                  return;
                }
                const isDup = expenseCategories?.some(
                  (c) =>
                    c.name.toLowerCase() === trimmed.toLowerCase() &&
                    (!editingCategory || c.id !== editingCategory.id)
                );
                if (isDup) {
                  toast.error(`A category named "${trimmed}" already exists`);
                  return;
                }
                const kwArray = newCategoryKeywords
                  .split(',')
                  .map((k) => k.trim().toLowerCase())
                  .filter(Boolean);

                try {
                  await upsertExpenseCategory.mutateAsync({
                    id: editingCategory?.id,
                    name: trimmed,
                    keywords: kwArray,
                    sort_order: parseInt(newCategorySortOrder) || 100,
                    is_active: editingCategory ? editingCategory.is_active : true,
                  });
                  toast.success(editingCategory ? 'Category updated' : 'Category created');
                  setShowCategoryModal(false);
                  refetchCategories();
                } catch (err: unknown) {
                  toast.error(err instanceof Error ? err.message : 'Failed to save category');
                }
              }}
              className="space-y-4"
            >
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Category Name *</label>
                <Input
                  type="text"
                  required
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="e.g. Courier Charge, Petrol, Office Supplies"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Category Name *"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Keywords for Auto-Match</label>
                <Input
                  type="text"
                  value={newCategoryKeywords}
                  onChange={(e) => setNewCategoryKeywords(e.target.value)}
                  placeholder="e.g. courier, shipping, delivery, post"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Keywords for Auto-Match"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Comma-separated keywords matched against sheet rows during import.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Sort Order</label>
                <Input
                  type="number"
                  value={newCategorySortOrder}
                  onChange={(e) => setNewCategorySortOrder(e.target.value)}
                  placeholder="100"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Sort Order"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <PermissionGate permission="master_config.manage"><button
                  type="button"
                  onClick={() => setShowCategoryModal(false)}
                  className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
                >
                  Cancel
                </button></PermissionGate>
                <button
                  type="submit"
                  disabled={upsertExpenseCategory.isPending}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
                >
                  {upsertExpenseCategory.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingCategory ? 'Save Changes' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: ADD PRODUCT TYPE */}
      {showAddTypeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-field-guide-scope>
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
            <div className="flex items-start justify-between gap-3 pr-8">
              <h3 className="mb-4 text-base font-semibold text-foreground">Add product category</h3>
              <UserGuide compact />
            </div>
            <p className="mb-4 text-sm leading-relaxed text-muted-foreground">Create a product family, such as Jersey or Helmet. After saving, open Category setup, configure its entry fields and publish the setup before adding new products.</p>
            <form onSubmit={handleCreateProductType} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Category name *</label>
                <Input
                  type="text"
                  required
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)}
                  placeholder="e.g. Hoodie, Dress, Oversized Tee"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Category name *"
                />
                <FieldHint>Use a clear family name. Check the existing categories to avoid a duplicate.</FieldHint>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Description</label>
                <Textarea
                  value={newTypeDesc}
                  onChange={(e) => setNewTypeDesc(e.target.value)}
                  placeholder="Describe what belongs in this category"
                  rows={2}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Description"
                />
                <FieldHint>Optional guidance for your team about the products included in this family.</FieldHint>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <PermissionGate permission="master_config.manage"><button
                  type="button"
                  onClick={() => setShowAddTypeModal(false)}
                  className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
                >
                  Cancel
                </button></PermissionGate>
                <button
                  type="submit"
                  disabled={createProductType.isPending}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
                >
                  {createProductType.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Create category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: ADD ATTRIBUTE */}
      {showAddAttrModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-field-guide-scope>
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
            <div className="flex items-start justify-between gap-3 pr-8">
              <h3 className="mb-4 text-base font-semibold text-foreground">Add New Attribute Definition</h3>
              <UserGuide compact />
            </div>
            <form onSubmit={handleCreateAttribute} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Attribute Name *</label>
                <Input
                  type="text"
                  required
                  value={newAttrName}
                  onChange={(e) => setNewAttrName(e.target.value)}
                  placeholder="e.g. Hood Type, Length, Fabric Blend"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Attribute Name *"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Input Type</label>
                <Select
                  value={newAttrType}
                  onChange={(e) => setNewAttrType(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Input Type"
                >
                  <option value="select">Dropdown Select (Predefined Options)</option>
                  <option value="text">Free Text Input</option>
                  <option value="number">Number</option>
                  <option value="boolean">Yes / No Toggle</option>
                </Select>
              </div>
              {newAttrType === 'select' && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">Options (Comma Separated)</label>
                  <Input
                    type="text"
                    value={newAttrOptions}
                    onChange={(e) => setNewAttrOptions(e.target.value)}
                    placeholder="e.g. Option 1, Option 2, Option 3"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    helpLabel="Options (Comma Separated)"
                  />
                </div>
              )}
              <div className="flex items-center gap-2 pt-1">
                <Input
                  type="checkbox"
                  id="new-attr-variant"
                  checked={newAttrIsVariant}
                  onChange={(e) => setNewAttrIsVariant(e.target.checked)}
                  className="rounded border-input text-primary focus:ring-primary h-4 w-4"
                  helpLabel="Variant-level field"
                />
                <label htmlFor="new-attr-variant" className="text-sm font-medium text-foreground cursor-pointer">
                  Is Variant-Level Attribute? (e.g. Size, Color)
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <PermissionGate permission="master_config.manage"><button
                  type="button"
                  onClick={() => setShowAddAttrModal(false)}
                  className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
                >
                  Cancel
                </button></PermissionGate>
                <button
                  type="submit"
                  disabled={createAttribute.isPending}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
                >
                  {createAttribute.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Create Attribute
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: ADD / EDIT TEAM */}
      {showAddTeamModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-field-guide-scope>
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
            <div className="flex items-start justify-between gap-3 pr-8">
              <h3 className="mb-4 text-base font-semibold text-foreground">
                {editingTeam ? 'Edit Team' : 'Add New Team'}
              </h3>
              <UserGuide compact />
            </div>
            <form onSubmit={handleSaveTeam} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Team Name *</label>
                <Input
                  type="text"
                  required
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  placeholder="e.g. Manchester United, Liverpool"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Team Name *"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Sport</label>
                <Input
                  type="text"
                  value={newTeamSport}
                  onChange={(e) => setNewTeamSport(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Sport"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <PermissionGate permission="master_config.manage"><button
                  type="button"
                  onClick={() => setShowAddTeamModal(false)}
                  className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
                >
                  Cancel
                </button></PermissionGate>
                <button
                  type="submit"
                  disabled={createTeam.isPending || updateTeam.isPending}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
                >
                  {(createTeam.isPending || updateTeam.isPending) && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingTeam ? 'Save Changes' : 'Add Team'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: ADD / EDIT STAFF */}
      {showAddStaffModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-field-guide-scope>
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
            <div className="flex items-start justify-between gap-3 pr-8">
              <h3 className="mb-4 text-base font-semibold text-foreground">
                {editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}
              </h3>
              <UserGuide compact />
            </div>
            <form onSubmit={handleSaveStaff} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Name *</label>
                <Input
                  type="text"
                  required
                  autoFocus
                  value={newStaffName}
                  onChange={(e) => setNewStaffName(e.target.value)}
                  placeholder="e.g. Rahul Menon"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Name *"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  Role Label <span className="text-xs text-muted-foreground">(optional)</span>
                </label>
                <Input
                  type="text"
                  value={newStaffRoleLabel}
                  onChange={(e) => setNewStaffRoleLabel(e.target.value)}
                  placeholder="e.g. Sales Executive, Floor Staff"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Role Label (optional)"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <PermissionGate permission="master_config.manage"><button
                  type="button"
                  onClick={() => setShowAddStaffModal(false)}
                  className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
                >
                  Cancel
                </button></PermissionGate>
                <button
                  type="submit"
                  disabled={createStaffMember.isPending || updateStaffMember.isPending}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
                >
                  {(createStaffMember.isPending || updateStaffMember.isPending) && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  {editingStaff ? 'Save Changes' : 'Add Staff Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: ADD / EDIT AUDIENCE */}
      {showAddAudienceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-field-guide-scope>
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
            <div className="flex items-start justify-between gap-3 pr-8">
              <h3 className="mb-4 text-base font-semibold text-foreground">
                {editingAudience ? 'Edit Target Audience' : 'Add Target Audience'}
              </h3>
              <UserGuide compact />
            </div>
            <form onSubmit={handleSaveAudience} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Audience Name *</label>
                <Input
                  type="text"
                  required
                  value={newAudienceName}
                  onChange={(e) => setNewAudienceName(e.target.value)}
                  placeholder="e.g. Male, Female, Unisex"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Audience Name *"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Code (Optional)</label>
                <Input
                  type="text"
                  value={newAudienceCode}
                  onChange={(e) => setNewAudienceCode(e.target.value)}
                  placeholder="e.g. MALE, UNISEX"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm uppercase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Code (Optional)"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <PermissionGate permission="master_config.manage"><button
                  type="button"
                  onClick={() => setShowAddAudienceModal(false)}
                  className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
                >
                  Cancel
                </button></PermissionGate>
                <button
                  type="submit"
                  disabled={createTargetAudience.isPending || updateTargetAudience.isPending}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
                >
                  {(createTargetAudience.isPending || updateTargetAudience.isPending) && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingAudience ? 'Save Changes' : 'Add Audience'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: ADD / EDIT AGE GROUP */}
      {showAddAgeGroupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-field-guide-scope>
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
            <div className="flex items-start justify-between gap-3 pr-8">
              <h3 className="mb-4 text-base font-semibold text-foreground">
                {editingAgeGroup ? 'Edit Age Group' : 'Add Age Group'}
              </h3>
              <UserGuide compact />
            </div>
            <form onSubmit={handleSaveAgeGroup} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Age Group Name *</label>
                <Input
                  type="text"
                  required
                  value={newAgeGroupName}
                  onChange={(e) => setNewAgeGroupName(e.target.value)}
                  placeholder="e.g. Adult, Kids, Toddler"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Age Group Name *"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Code (Optional)</label>
                <Input
                  type="text"
                  value={newAgeGroupCode}
                  onChange={(e) => setNewAgeGroupCode(e.target.value)}
                  placeholder="e.g. ADULT, KIDS"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm uppercase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Code (Optional)"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <PermissionGate permission="master_config.manage"><button
                  type="button"
                  onClick={() => setShowAddAgeGroupModal(false)}
                  className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
                >
                  Cancel
                </button></PermissionGate>
                <button
                  type="submit"
                  disabled={createAgeGroup.isPending || updateAgeGroup.isPending}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
                >
                  {(createAgeGroup.isPending || updateAgeGroup.isPending) && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingAgeGroup ? 'Save Changes' : 'Add Age Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: ADD / EDIT SIZE SET */}
      {showAddSizeSetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-field-guide-scope>
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
            <div className="flex items-start justify-between gap-3 pr-8">
              <h3 className="mb-4 text-base font-semibold text-foreground">
                {editingSizeSet ? 'Edit Size Set' : 'Add Size Set'}
              </h3>
              <UserGuide compact />
            </div>
            <form onSubmit={handleSaveSizeSet} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Size Set Name *</label>
                <Input
                  type="text"
                  required
                  value={newSizeSetName}
                  onChange={(e) => setNewSizeSetName(e.target.value)}
                  placeholder="e.g. Adult Standard"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Size Set Name *"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Code (Optional)</label>
                <Input
                  type="text"
                  value={newSizeSetCode}
                  onChange={(e) => setNewSizeSetCode(e.target.value)}
                  placeholder="e.g. ADULT_STD"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm uppercase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Code (Optional)"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Sizes (Comma Separated) *</label>
                <Input
                  type="text"
                  required
                  value={newSizeSetSizes}
                  onChange={(e) => setNewSizeSetSizes(e.target.value)}
                  placeholder="e.g. XS, S, M, L, XL"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Sizes (Comma Separated) *"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <PermissionGate permission="master_config.manage"><button
                  type="button"
                  onClick={() => setShowAddSizeSetModal(false)}
                  className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
                >
                  Cancel
                </button></PermissionGate>
                <button
                  type="submit"
                  disabled={createSizeSet.isPending || updateSizeSet.isPending}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
                >
                  {(createSizeSet.isPending || updateSizeSet.isPending) && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingSizeSet ? 'Save Changes' : 'Add Size Set'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
